import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { CHATBOT_SECTION_META, orderedChatbotSections } from "@/lib/chatbot-sections"
import { CHATBOT_SECTIONS, type ChatbotSection } from "@/lib/types"
import { cn } from "@/lib/utils"

/**
 * Checkbox por seção do painel: o dono marca exatamente o que a pessoa pode
 * abrir. Usado no convite e na edição de acesso de um membro já aceito.
 * `grantableSections` limita as opções de quem administra: um coadministrador
 * só libera o que ele mesmo já tem.
 */
export function ChatbotFeaturePicker({ idPrefix, sections, onChange, grantableSections = [...CHATBOT_SECTIONS], disabled }: {
  idPrefix: string
  sections: ChatbotSection[]
  onChange: (next: ChatbotSection[]) => void
  grantableSections?: ChatbotSection[]
  disabled?: boolean
}) {
  function toggle(section: ChatbotSection, checked: boolean) {
    onChange(orderedChatbotSections(checked ? [...sections, section] : sections.filter((current) => current !== section)))
  }

  const limited = grantableSections.length < CHATBOT_SECTIONS.length

  return (
    <div className="space-y-3">
      <div className="grid gap-2 sm:grid-cols-2">
        {orderedChatbotSections(grantableSections).map((section) => {
          const meta = CHATBOT_SECTION_META[section]
          const inputId = `${idPrefix}-${section}`
          const checked = sections.includes(section)
          return (
            <div
              key={section}
              className={cn("flex items-start gap-3 rounded-xl border px-3.5 py-3 transition", checked ? "border-primary/40 bg-primary/5" : "field-bg border-border")}
            >
              <Checkbox id={inputId} className="mt-0.5" checked={checked} disabled={disabled} onCheckedChange={(next) => toggle(section, next === true)} />
              <div className="min-w-0">
                <Label htmlFor={inputId} className="block text-[12.5px] font-semibold text-foreground">{meta.label}</Label>
                <p className="muted mt-0.5 text-[11px] leading-4">{meta.description}</p>
              </div>
            </div>
          )
        })}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className={cn("text-[11px]", sections.length ? "muted" : "text-warning")}>
          {sections.length ? `${sections.length} de ${grantableSections.length} seções liberadas` : "Selecione ao menos uma seção para a pessoa conseguir entrar no assistente."}
        </p>
        <div className="flex shrink-0 gap-3 text-[11px] font-semibold">
          <button type="button" className="text-primary hover:underline disabled:cursor-not-allowed disabled:opacity-50" disabled={disabled || sections.length === grantableSections.length} onClick={() => onChange(orderedChatbotSections(grantableSections))}>Marcar todas</button>
          <button type="button" className="text-muted-foreground hover:underline disabled:cursor-not-allowed disabled:opacity-50" disabled={disabled || !sections.length} onClick={() => onChange([])}>Limpar</button>
        </div>
      </div>
      {limited && <p className="muted text-[11px] leading-4">Você só pode liberar seções que já tem neste assistente. Peça ao dono para ampliar o seu acesso se precisar de mais.</p>}
    </div>
  )
}
